import { useState, useRef, useEffect } from 'react';
import { Handshake, Search, X, Check } from 'lucide-react';
import { mediaUrl } from '../lib/api-service';

interface PartnerOption {
  id: number;
  name: string;
  slug: string;
  logo?: string;
}

interface PartnerAutocompleteProps {
  partners: PartnerOption[];
  value: string; // slug
  onChange: (slug: string) => void;
  placeholder?: string;
}

export function PartnerAutocomplete({ partners, value, onChange, placeholder = 'Caută prieten...' }: PartnerAutocompleteProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  const selected = partners.find((p) => p.slug === value);
  const filtered = partners.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => { setOpen(!open); setSearch(''); }}
        className="flex w-full items-center gap-2 rounded-lg border bg-white px-3 py-2.5 text-left text-sm outline-none transition-colors focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20"
      >
        {selected ? (
          <>
            <PartnerAvatar partner={selected} size="sm" />
            <span className="flex-1 truncate">{selected.name}</span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onChange(''); }}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </>
        ) : (
          <>
            <Search className="h-4 w-4 text-muted-foreground" />
            <span className="flex-1 text-muted-foreground">{placeholder}</span>
          </>
        )}
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border bg-white shadow-lg">
          <div className="border-b px-3 py-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Caută..."
              autoFocus
              className="w-full text-sm outline-none"
              style={{ fontFamily: 'var(--font-secondary)' }}
            />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {/* "All" option */}
            <button
              type="button"
              onClick={() => { onChange(''); setOpen(false); }}
              className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-gray-50 ${!value ? 'bg-[var(--vivid-purple)]/5' : ''}`}
            >
              <div className="flex h-7 w-7 items-center justify-center rounded-md bg-gray-100">
                <Handshake className="h-3.5 w-3.5 text-muted-foreground" />
              </div>
              <span className="flex-1">Toți prietenii</span>
              {!value && <Check className="h-4 w-4 text-[var(--vivid-purple)]" />}
            </button>
            {filtered.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => { onChange(p.slug); setOpen(false); }}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-gray-50 ${value === p.slug ? 'bg-[var(--vivid-purple)]/5' : ''}`}
              >
                <PartnerAvatar partner={p} size="sm" />
                <span className="flex-1 truncate">{p.name}</span>
                {value === p.slug && <Check className="h-4 w-4 text-[var(--vivid-purple)]" />}
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="px-3 py-4 text-center text-xs text-muted-foreground">Niciun prieten găsit</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Multi-select variant for ActivityForm */
interface PartnerMultiSelectProps {
  partners: PartnerOption[];
  selectedIds: number[];
  onChange: (id: number) => void;
  excludeIds?: number[];
}

export function PartnerMultiSelect({ partners, selectedIds, onChange, excludeIds = [] }: PartnerMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  const available = partners.filter((p) =>
    !excludeIds.includes(p.id) && p.name.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => { setOpen(!open); setSearch(''); }}
        className="flex w-full items-center gap-2 rounded-lg border bg-white px-3 py-2.5 text-left text-sm outline-none transition-colors focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20"
      >
        <Search className="h-4 w-4 text-muted-foreground" />
        <span className="text-muted-foreground">Selectează prieten...</span>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border bg-white shadow-lg">
          <div className="border-b px-3 py-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Caută..."
              autoFocus
              className="w-full text-sm outline-none"
            />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {available.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => { onChange(p.id); setOpen(false); setSearch(''); }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-gray-50"
              >
                <PartnerAvatar partner={p} size="sm" />
                <span className="flex-1 truncate">{p.name}</span>
              </button>
            ))}
            {available.length === 0 && (
              <p className="px-3 py-4 text-center text-xs text-muted-foreground">Niciun prieten disponibil</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PartnerAvatar({ partner, size = 'sm' }: { partner: PartnerOption; size?: 'sm' | 'md' }) {
  const dim = size === 'sm' ? 'h-7 w-7' : 'h-9 w-9';
  const resolved = mediaUrl(partner.logo);
  if (resolved) {
    return <img src={resolved} alt="" className={`${dim} rounded-md border object-cover`} />;
  }
  return (
    <div className={`${dim} flex items-center justify-center rounded-md bg-[var(--vivid-purple)]/10`}>
      <Handshake className="h-3.5 w-3.5 text-[var(--vivid-purple)]" />
    </div>
  );
}
